import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useScrollToTop from "./../../hooks/useScollToTop";
import {
  checkResetPasswordToken,
  resetPassword,
} from "./../../services/passwordServices";
import backgroundImage from "./illustration_login.webp";
import {
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaKey,
} from "react-icons/fa";

function ResetPasswordPage() {
  useScrollToTop();
  const navigate = useNavigate();
  const { token } = useParams();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setIsCheckingToken(true);
        setError("");

        const result = await checkResetPasswordToken(token);

        if (!result?.valid) {
          setIsTokenValid(false);
          setError(
            result?.message || "Token de réinitialisation invalide ou expiré.",
          );
          return;
        }

        setIsTokenValid(true);
      } catch (err) {
        setIsTokenValid(false);
        setError("Impossible de vérifier le lien de réinitialisation.");
      } finally {
        setIsCheckingToken(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setIsTokenValid(false);
      setError("Token de réinitialisation manquant.");
      setIsCheckingToken(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!isTokenValid) {
      setError("Le lien de réinitialisation n'est pas valide.");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await resetPassword(token, password);

      if (result?.success) {
        const successText =
          result?.message ||
          "Votre mot de passe a bien été réinitialisé. Vous pouvez maintenant vous connecter.";

        setSuccessMessage(successText);

        setTimeout(() => {
          navigate("/login", {
            state: {
              successMessage: successText,
            },
          });
        }, 1800);
      } else {
        setError(
          result?.message ||
            "Impossible de réinitialiser le mot de passe pour le moment.",
        );
      }
    } catch (err) {
      setError("Impossible de réinitialiser le mot de passe pour le moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-slate-950"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

      <div className="relative z-10 flex min-h-screen items-start justify-center px-3 py-3 sm:px-4 sm:py-5 md:items-center md:py-8">
        <div className="w-full max-w-[980px] overflow-hidden rounded-2xl border border-white/10 bg-white/95 shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95 md:rounded-3xl">
          <div className="grid md:grid-cols-[0.88fr_1.12fr]">
            {/* Colonne branding desktop */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white lg:p-8">
              <div>
                <p className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Club Présence Pro
                </p>

                <h1 className="text-2xl font-black leading-tight lg:text-3xl">
                  Définissez un nouveau mot de passe en toute sécurité
                </h1>

                <p className="mt-5 max-w-md text-sm leading-6 text-white/80">
                  Choisissez un nouveau mot de passe pour sécuriser l’accès à
                  votre compte et reprendre la gestion de votre club.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Vérification du lien avant réinitialisation.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Choisissez un mot de passe simple à retenir et sécurisé.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Une fois validé, vous pourrez vous reconnecter
                    immédiatement.
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne contenu */}
            <div className="p-4 sm:p-6 md:p-7 lg:p-8">
              <div className="mx-auto w-full max-w-none">
                {/* Intro mobile */}
                <div className="mb-5 md:hidden">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 text-white shadow-lg backdrop-blur-sm">
                    <p className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                      Club Présence Pro
                    </p>

                    <h1 className="text-xl font-black leading-tight">
                      Définissez un nouveau mot de passe en toute sécurité
                    </h1>

                    <p className="mt-2 text-sm leading-6 text-white/80">
                      Choisissez un nouveau mot de passe pour sécuriser l’accès
                      à votre compte.
                    </p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Vérification du lien avant validation.
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Connexion possible juste après la mise à jour.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5 text-center md:text-left">
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">
                    Nouveau mot de passe
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Saisissez puis confirmez votre nouveau mot de passe.
                  </p>
                </div>

                {successMessage && (
                  <div
                    className="mb-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300"
                    role="alert"
                  >
                    {successMessage}
                  </div>
                )}

                {error && (
                  <div
                    className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20 dark:bg-blue-500">
                        <FaShieldAlt />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          Réinitialisation sécurisée
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Le lien reçu par email est vérifié avant modification.
                        </p>
                      </div>
                    </div>

                    {isCheckingToken ? (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        Vérification du lien de réinitialisation...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Nouveau mot de passe
                          </label>
                          <div className="relative">
                            <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type={showPassword ? "text" : "password"}
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              placeholder="Choisissez un nouveau mot de passe"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              autoComplete="new-password"
                              minLength={6}
                              required
                              disabled={!isTokenValid || isSubmitting}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((prev) => !prev)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                              aria-label={
                                showPassword
                                  ? "Masquer le mot de passe"
                                  : "Afficher le mot de passe"
                              }
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Minimum 6 caractères.
                          </p>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Confirmez le mot de passe
                          </label>
                          <div className="relative">
                            <FaKey className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              placeholder="Confirmez votre nouveau mot de passe"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              autoComplete="new-password"
                              minLength={6}
                              required
                              disabled={!isTokenValid || isSubmitting}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword((prev) => !prev)
                              }
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                              aria-label={
                                showConfirmPassword
                                  ? "Masquer la confirmation"
                                  : "Afficher la confirmation"
                              }
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isCheckingToken || !isTokenValid || isSubmitting}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    {isSubmitting
                      ? "Réinitialisation..."
                      : "Réinitialiser le mot de passe"}
                  </button>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Link
                        to="/login"
                        className="font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Retour à la connexion
                      </Link>

                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="text-left font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 sm:text-right"
                      >
                        Demander un nouveau lien
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
